import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ChatService } from '@celerofinancas/service-utils';
import { UserState } from '@celerofinancas/state-management';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import {
  CSAT_SURVEY_ANONYMOUS_USER,
  CSAT_SURVEY_BY_ROUTE,
  CSAT_SURVEY_STORAGE_PREFIX,
} from '@shared/constants/csat-surveys';
import { PolicyAcceptanceService } from '@shared/services/policy-acceptance/policy-acceptance.service';
import { take } from 'rxjs';

/**
 * Starts the Intercom CSAT survey mapped to the route the user is on.
 *
 * Surveys only reach users who already accepted the terms of use: the
 * acceptance is checked here, on the user, so the guard holds for every screen
 * regardless of which component asked for the survey.
 *
 * A survey is started at most once per user, per survey — not per route and
 * not per session. `Intercom('startSurvey')` bypasses the survey's audience
 * rules and frequency settings, so it always renders when called: preventing
 * repeated prompts is entirely up to this guard.
 */
@Injectable({
  providedIn: 'root',
})
export class CsatSurveyService {
  /**
   * Chat service
   */
  private readonly chat = inject(ChatService);

  /**
   * Router service
   */
  private readonly router = inject(Router);

  /**
   * Store service, used to scope the guard to the logged user
   */
  private readonly store = inject(Store);

  /**
   * Terms of use acceptance, the precondition for collecting CSAT
   */
  private readonly policyAcceptance = inject(PolicyAcceptanceService);

  /**
   * Surveys already started in this page load, keyed by user and survey.
   *
   * Backs the guard when web storage is unavailable — the application runs
   * embedded in an iframe on the CAIXA portal, where storage access can be
   * partitioned or denied by the browser.
   */
  private readonly startedSurveys = new Set<string>();

  /**
   * Whether retired survey guards were already discarded in this page load
   */
  private discardedRetiredGuards = false;

  /**
   * Starts the CSAT survey mapped to the current route, if the logged user
   * accepted the terms of use and the survey was not started for them yet.
   *
   * The acceptance is read once the user is loaded, and a user who has not
   * accepted is simply skipped — no survey is queued waiting for the
   * acceptance. Someone accepting the terms right now is answering the initial
   * journey modal, which is no moment to ask how the product is doing; the
   * survey comes back on the next navigation into a mapped route.
   */
  public triggerForCurrentRoute(): void {
    if (!environment.chat?.enabled) {
      return;
    }

    this.policyAcceptance.hasAcceptedOnceUserLoaded$
      .pipe(take(1))
      .subscribe((hasAcceptedTerms: boolean) => {
        if (hasAcceptedTerms) {
          this.startSurveyForCurrentRoute();
        }
      });
  }

  /**
   * Starts the survey mapped to the route, guarding against repeated prompts
   */
  private startSurveyForCurrentRoute(): void {
    this.discardRetiredGuards();

    const surveyId = CSAT_SURVEY_BY_ROUTE[this.getCurrentPath()];

    if (!surveyId) {
      return;
    }

    const key = this.getGuardKey(surveyId);

    if (this.wasAlreadyStarted(key)) {
      return;
    }

    this.markAsStarted(key);
    this.chat.startSurvey(surveyId);
  }

  /**
   * Current route path, without query params, fragment or trailing slash
   */
  private getCurrentPath(): string {
    const [path] = this.router.url.split(/[?#]/);

    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }

  /**
   * Guard key for a survey, scoped to the logged user.
   *
   * The user is part of the key because the guard now persists across
   * sessions: without it, the first person to answer on a shared device would
   * silently suppress the survey for everyone else who logs in there.
   *
   * @param surveyId - Intercom survey id
   */
  private getGuardKey(surveyId: string): string {
    const user = this.store.selectSnapshot(UserState.getState);
    const userId = user?.pk ? String(user.pk) : CSAT_SURVEY_ANONYMOUS_USER;

    return `${CSAT_SURVEY_STORAGE_PREFIX}${userId}:${surveyId}`;
  }

  /**
   * Drops stored guards of surveys that are no longer configured.
   *
   * Keeps a future survey from being suppressed by a retired one and stops the
   * guards from piling up in storage: when a survey is replaced, its id leaves
   * `CSAT_SURVEY_BY_ROUTE` and the old entries are cleaned up on the next run,
   * with no manual data cleanup needed.
   */
  private discardRetiredGuards(): void {
    if (this.discardedRetiredGuards) {
      return;
    }

    this.discardedRetiredGuards = true;
    const configuredSurveys = new Set(Object.values(CSAT_SURVEY_BY_ROUTE));

    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(CSAT_SURVEY_STORAGE_PREFIX))
        .filter((key) => !configuredSurveys.has(key.slice(key.lastIndexOf(':') + 1)))
        .forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage unavailable — nothing persisted to clean up.
    }
  }

  /**
   * Whether the survey was already started for this user
   *
   * @param key - guard key returned by `getGuardKey`
   */
  private wasAlreadyStarted(key: string): boolean {
    if (this.startedSurveys.has(key)) {
      return true;
    }

    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  }

  /**
   * Records the survey as started for this user
   *
   * @param key - guard key returned by `getGuardKey`
   */
  private markAsStarted(key: string): void {
    this.startedSurveys.add(key);

    try {
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // Storage unavailable — the in-memory guard above still holds for this page load.
    }
  }
}
